/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/anchor-is-valid */
// Reevaluate the elements breaking these rules when there's time
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Button from 'components/shared/Button';

import { realNumber } from 'utils/math_util';

type FilterButtonsProps = {
  filters: string[];
  filter: string;
  handleSelectFilter: (filter: string) => void;
};

function FilterButtons({ filters, filter, handleSelectFilter }: FilterButtonsProps) {
  const elemRef = useRef<HTMLDivElement>(null);

  const [elem, setElem] = useState<HTMLDivElement | null>(null);
  const [scrollPanel, setScrollPanel] = useState<HTMLDivElement | null>(null);
  const [prevArrow, setPrevArrow] = useState<HTMLAnchorElement | null>(null);
  const [nextArrow, setNextArrow] = useState<HTMLAnchorElement | null>(null);

  const [elementWidth, setElementWidth] = useState<number | null>(null);
  const [scrollWidth, setScrollWidth] = useState<number | null>(null);

  const [endVal, setEndVal] = useState<string | null>(null);

  const [scrollPos, setScrollPos] = useState<number>(0);
  const [scrollStyle, setScrollStyle] = useState({});

  const [showScrollArrows, setShowScrollArrows] = useState(false);

  useEffect(() => {
    if (filters.length > 0 && elemRef.current) {
      setElem(elemRef.current);
    }
  }, []);

  useEffect(() => {
    if (elem) {
      setElementWidth(elem.offsetWidth);
      setScrollPanel(elem.querySelector<HTMLDivElement>('.scroll-panel'));
    }
  }, [elem]);

  useEffect(() => {
    function handleElemResize() {
      if (elemRef.current) {
        setElem(elemRef.current);
      }
    }

    if (scrollPanel) {
      const scrollPanelOffsetWidth = Math.round(
        Array.from(scrollPanel.children).reduce((w, c) => {
          const elemstyle = window.getComputedStyle(c);
          return (
            w +
            parseFloat(elemstyle.getPropertyValue('width')) +
            parseFloat(elemstyle.getPropertyValue('margin-left')) +
            parseFloat(elemstyle.getPropertyValue('margin-right'))
          );
        }, 0)
      );

      setScrollWidth(scrollPanelOffsetWidth);
      let styles = window.getComputedStyle(scrollPanel);
      setEndVal(styles.getPropertyValue('--translateTo'));

      scrollPanel.addEventListener('animationend', () => {
        styles = window.getComputedStyle(scrollPanel);
        setEndVal(styles.getPropertyValue('--translateTo'));

        scrollPanel.classList.remove('goscroll');
      });

      const elemObserver = new MutationObserver(handleElemResize);

      elemObserver.observe(scrollPanel, { attributes: true });
    }
  }, [scrollPanel]);

  useEffect(() => {
    const hasGoodValues = endVal && realNumber(parseInt(endVal)) && scrollWidth && elementWidth;
    if (hasGoodValues && scrollPanel) {
      setScrollStyle({ '--translateTo': endVal, '--translateFrom': endVal, '--pinPos': endVal });

      if (prevArrow) {
        prevArrow.classList.toggle('limit', parseInt(endVal, 10) === 0);
      }

      if (nextArrow) {
        nextArrow.classList.toggle(
          'limit',
          parseInt(endVal, 10) <= (scrollWidth - (elementWidth - 20)) * -1
        );
      }
    }
  }, [endVal]);

  useEffect(() => {
    if (elementWidth && scrollWidth) {
      setShowScrollArrows(elementWidth < scrollWidth);
    }
  }, [elementWidth, scrollWidth]);

  useEffect(() => {
    if (elem) {
      setPrevArrow(elem.querySelector<HTMLAnchorElement>('.scroll-arrow.prev'));
      setNextArrow(elem.querySelector<HTMLAnchorElement>('.scroll-arrow.next'));
      elem.classList.toggle('sidescroll', showScrollArrows);
      elem.classList.toggle('buttons', !showScrollArrows);
    }

    if (scrollPanel) {
      if (!showScrollArrows) {
        scrollPanel.classList.remove('goscroll');
        setScrollStyle({ '--translateTo': 0, '--translateFrom': 0, '--pinPos': 0 });
      }
    }
  }, [elem, showScrollArrows]);

  // TODO: Refactor this functionality
  // When converting this component to TS, I just did what was necessary to get it running
  // It wasn't working totally right before this, as I think the logic is missing some
  // pieces. I don't think 20 is the right padding on the element, and the last item
  // in the scroll panel is cut off when trying to lock the _first_ element to
  // the left side of the panel. We should be locking the _next_ element to the end
  // of the panel when moving to the next item, and doing this logic only when moving
  // to the previous element
  function handleScrollArrowClick(e: React.MouseEvent) {
    if (!scrollPanel || !elementWidth || !scrollWidth) return;

    const { currentTarget } = e;
    const dir = currentTarget.classList.contains('prev') ? 1 : -1;
    setScrollPos(Math.max(scrollPos + 1 * dir * -1, 0));

    const translateFrom =
      parseInt(window.getComputedStyle(scrollPanel).getPropertyValue('--translateFrom')) * -1;

    const filterButtons = Array.from(scrollPanel.children) as HTMLElement[];

    const nextFirstButton =
      filterButtons.find((button) => {
        return (
          (dir < 0 &&
            button.offsetLeft < translateFrom + (elementWidth - 20) * (dir * -1) &&
            button.offsetLeft + button.offsetWidth >
              translateFrom + (elementWidth - 20) * (dir * -1)) ||
          (dir > 0 && button.offsetLeft > translateFrom + (elementWidth - 20) * (dir * -1))
        );
      }) ||
      filterButtons.find((button, i, buttons) => {
        const prevButton = i > 0 ? buttons[i - 1] : undefined;
        return (
          (dir < 0 &&
            button.offsetLeft > translateFrom + (elementWidth - 20) * (dir * -1) &&
            prevButton &&
            prevButton.offsetLeft + prevButton.offsetWidth <=
              translateFrom + (elementWidth - 20) * (dir * -1)) ||
          (dir > 0 && button.offsetLeft > translateFrom + (elementWidth - 20) * (dir * -1))
        );
      });

    const translateLimit = (scrollWidth - (elementWidth - 20)) * -1;
    const translateTo = Math.min(
      Math.max(
        ((nextFirstButton && nextFirstButton.offsetLeft) || 0) * -1,
        Math.min(translateLimit, 0)
      ),
      0
    );

    scrollPanel.style.setProperty('--translateTo', `${translateTo}px`);
    scrollPanel.classList.add('goscroll');
  }

  const checkFilterGroups = useMemo(() => {
    return filters.filter((group) => group !== 'All');
  }, [filters]);

  return (
    <div
      ref={elemRef}
      className={`product-filter-buttons ${checkFilterGroups.length > 0 ? 'sidescroll' : ''}`}
    >
      {showScrollArrows && checkFilterGroups.length > 0 && (
        <a className="scroll-arrow prev limit" onClick={handleScrollArrowClick}>
          <FontAwesomeIcon icon="caret-left" />
        </a>
      )}
      {checkFilterGroups.length > 0 && (
        <div className="scroll-wrapper">
          <div className="scroll-panel" data-scroll-pos={scrollPos} style={scrollStyle}>
            {filters.length > 1 &&
              filters.map((filterGroup) => (
                <Button
                  key={filterGroup}
                  type="bare"
                  className={`${filter === filterGroup ? 'active' : null}`}
                  onClick={() => handleSelectFilter(filterGroup)}
                >
                  {filterGroup}
                </Button>
              ))}
          </div>
        </div>
      )}
      {showScrollArrows && checkFilterGroups.length > 0 && (
        <a className="scroll-arrow next" onClick={handleScrollArrowClick}>
          <FontAwesomeIcon icon="caret-right" />
        </a>
      )}
    </div>
  );
}

export default FilterButtons;
